import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Typography,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  ListAlt as ListIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { projectService } from '../../services/project.service';
import { Project } from '../../types/project.types';
import { Mold } from '../../types';

const Glyph = ({ icon, size = '1.2rem' }: { icon: string; size?: string }) => (
  <Box
    component="span"
    className="material-symbols-outlined"
    sx={{
      fontSize: size,
      verticalAlign: 'middle',
      userSelect: 'none',
    }}
  >
    {icon}
  </Box>
);

export const ProjectDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { projectId } = useParams<{ projectId: string }>();
  
  const [project, setProject] = useState<Project | null>(null);
  const [molds, setMolds] = useState<Mold[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0); // ⭐ 탭 상태

  useEffect(() => {
    if (projectId) {
      loadProjectDetails();
    }
  }, [projectId]);

  const loadProjectDetails = async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      const projectData = await projectService.getProjectById(projectId);
      setProject(projectData);
      
      if (projectData) {
        const moldData = await projectService.getProjectMolds(projectId);
        setMolds(moldData);
      }
    } catch (error) {
      console.error('프로젝트 로드 실패:', error);
      alert('프로젝트를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/projects/${projectId}/edit`);
  };

  const handleDelete = async () => {
    if (!projectId || !project) return;
    
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    
    try {
      await projectService.deleteProject(projectId);
      alert('프로젝트가 삭제되었습니다.');
      navigate('/projects');
    } catch (error: any) {
      alert(error.message || '프로젝트 삭제에 실패했습니다.');
    }
  };

  const getStatusConfig = (status: string) => {
    const configs = {
      '계획': {
        label: '계획',
        color: '#FFFFFF',
        bgColor: '#3b82f6',
        icon: 'edit_note'
      },
      '진행중': {
        label: '진행중',
        color: '#FFFFFF',
        bgColor: '#10b981',
        icon: 'play_circle'
      },
      '완료': {
        label: '완료',
        color: '#FFFFFF',
        bgColor: '#6b7280',
        icon: 'check_circle'
      },
      '보류': {
        label: '보류',
        color: '#FFFFFF',
        bgColor: '#f59e0b',
        icon: 'cancel'
      }
    };
    return configs[status as keyof typeof configs] || configs['계획'];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: 4 }}>
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${alpha(theme.palette.error.light, 0.22)}`,
            textAlign: 'center',
            py: 8,
          }}
        >
          <CardContent>
            <Glyph icon="error_outline" size="3rem" />
            <Typography variant="h6" sx={{ mt: 2 }}>
              프로젝트를 찾을 수 없습니다
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const statusConfig = getStatusConfig(project.status);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 }, p: { xs: 2, md: 3 } }}>
      {/* 헤더 */}
      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          mb: { xs: 3, sm: 4 },
          gap: 2,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/projects')}
          sx={{ textTransform: 'none' }}
        >
          목록으로
        </Button>
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              flex: { xs: 1, sm: 'none' },
            }}
          >
            수정
          </Button>
          <Button
            variant="outlined"
            startIcon={<FolderIcon />}
            onClick={() => navigate(`/projects/${projectId}/files`)}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              flex: { xs: 1, sm: 'none' },
            }}
          >
            파일 관리
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              flex: { xs: 1, sm: 'none' },
            }}
          >
            삭제
          </Button>
        </Box>
      </Box>

      {/* ⭐ 탭 네비게이션 */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab 
            icon={<InfoIcon />} 
            label="프로젝트 정보" 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
          <Tab 
            icon={<ListIcon />} 
            label={`연결된 금형 (${molds.length})`}
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
        </Tabs>
      </Box>

      {/* ⭐ 탭 컨텐츠 */}
      
      {/* 탭 0: 프로젝트 정보 */}
      {activeTab === 0 && (
        <>
          {/* 헤더 카드 */}
          <Card
            elevation={0}
            sx={{
              mb: 3,
              border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
              backgroundImage: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.light,
                0.05
              )} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              <Box 
                sx={{ 
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'flex-start' },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography 
                    variant="h4" 
                    fontWeight={600} 
                    gutterBottom
                    sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
                  >
                    {project.projectName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    프로젝트 번호: {project.projectNo}
                  </Typography>
                </Box>
                <Chip
                  icon={<Glyph icon={statusConfig.icon} size="1rem" />}
                  label={statusConfig.label}
                  sx={{
                    backgroundColor: statusConfig.bgColor,
                    color: statusConfig.color,
                    fontWeight: 500,
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* 정보 박스 - 컴팩트 레이아웃 */}
          <Card
            elevation={0}
            sx={{
              mb: 3,
              border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
              backgroundImage: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.light,
                0.05
              )} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography 
                variant="h6" 
                fontWeight={600} 
                gutterBottom
                sx={{ 
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Glyph icon="info" size="1.2rem" />
                프로젝트 정보
              </Typography>

              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' },
                gap: { xs: 2, sm: 0 },
              }}>
                {/* 프로젝트 번호 */}
                <Box sx={{ display: 'contents' }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      bgcolor: alpha(theme.palette.grey[500], 0.05),
                      borderRadius: { xs: 1, sm: 0 },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      minWidth: { sm: 180 },
                    }}
                  >
                    <Glyph icon="tag" size="1rem" />
                    프로젝트 번호
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {project.projectNo}
                  </Typography>
                </Box>

                <Divider sx={{ gridColumn: '1 / -1', display: { xs: 'none', sm: 'block' } }} />

                {/* 프로젝트 타입 */}
                <Box sx={{ display: 'contents' }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      bgcolor: alpha(theme.palette.grey[500], 0.05),
                      borderRadius: { xs: 1, sm: 0 },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Glyph icon="category" size="1rem" />
                    프로젝트 타입
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {project.projectType || '신규'}
                  </Typography>
                </Box>

                <Divider sx={{ gridColumn: '1 / -1', display: { xs: 'none', sm: 'block' } }} />

                {/* 고객사 */}
                <Box sx={{ display: 'contents' }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      bgcolor: alpha(theme.palette.grey[500], 0.05),
                      borderRadius: { xs: 1, sm: 0 },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Glyph icon="business" size="1rem" />
                    고객사
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {project.customerName || '-'}
                  </Typography>
                </Box>

                <Divider sx={{ gridColumn: '1 / -1', display: { xs: 'none', sm: 'block' } }} />

                {/* 연결된 금형 */}
                <Box sx={{ display: 'contents' }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      bgcolor: alpha(theme.palette.grey[500], 0.05),
                      borderRadius: { xs: 1, sm: 0 },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Glyph icon="precision_manufacturing" size="1rem" />
                    연결된 금형
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {molds.length}개
                  </Typography>
                </Box>

                <Divider sx={{ gridColumn: '1 / -1', display: { xs: 'none', sm: 'block' } }} />

                {/* 시작일 */}
                <Box sx={{ display: 'contents' }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      bgcolor: alpha(theme.palette.grey[500], 0.05),
                      borderRadius: { xs: 1, sm: 0 },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Glyph icon="calendar_today" size="1rem" />
                    시작일
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {project.startDate ? project.startDate.toDate().toLocaleDateString('ko-KR') : '-'}
                  </Typography>
                </Box>

                <Divider sx={{ gridColumn: '1 / -1', display: { xs: 'none', sm: 'block' } }} />

                {/* 목표일 */}
                <Box sx={{ display: 'contents' }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      bgcolor: alpha(theme.palette.grey[500], 0.05),
                      borderRadius: { xs: 1, sm: 0 },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Glyph icon="event" size="1rem" />
                    목표일
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {project.targetDate ? project.targetDate.toDate().toLocaleDateString('ko-KR') : '-'}
                  </Typography>
                </Box>
              </Box>

              {/* 프로젝트 설명 */}
              {project.description && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        fontWeight: 500,
                      }}
                    >
                      <Glyph icon="description" size="1rem" />
                      프로젝트 설명
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ 
                        whiteSpace: 'pre-wrap', 
                        lineHeight: 1.8,
                        px: 2,
                        py: 1.5,
                        bgcolor: alpha(theme.palette.grey[500], 0.03),
                        borderRadius: 1,
                        border: `1px solid ${alpha(theme.palette.grey[500], 0.1)}`,
                      }}
                    >
                      {project.description}
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* 탭 1: 금형 목록 */}
      {activeTab === 1 && (
        <>
          {/* 헤더 카드 */}
          <Card
            elevation={0}
            sx={{
              mb: 3,
              border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
              backgroundImage: `linear-gradient(135deg, ${alpha(
                theme.palette.primary.light,
                0.05
              )} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
            }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                <Glyph icon="view_list" size="1.5rem" />
                <Typography 
                  variant="h5" 
                  fontWeight={600}
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  연결된 금형 ({molds.length})
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {molds.length === 0 ? (
            <Card
              elevation={0}
              sx={{
                border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
                backgroundImage: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.light,
                  0.05
                )} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    textAlign: 'center',
                    py: { xs: 6, sm: 8 },
                    backgroundColor: alpha(theme.palette.grey[500], 0.05),
                    borderRadius: 2,
                  }}
                >
                  <Glyph icon="inbox" size="3rem" />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    연결된 금형이 없습니다
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: { xs: 2, md: 3 },
            }}>
                {molds.map((mold) => {
                  const lifePercent = Math.round((mold.currentShots / mold.designLife) * 100);
                  
                  return (
                    <Card
                      key={mold.id}
                      elevation={0}
                      onClick={() => navigate(`/molds/${mold.id}`)}
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        border: `1px solid ${alpha(theme.palette.grey[500], 0.22)}`,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 16px ${alpha(theme.palette.primary.dark, 0.15)}`,
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                    >
                      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box 
                          sx={{ 
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'flex-start' },
                            gap: 2,
                          }}
                        >
                          <Box flex={1}>
                            <Typography 
                              variant="h6" 
                              fontWeight={600} 
                              gutterBottom
                              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                            >
                              {mold.moldNumber}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {mold.moldName}
                            </Typography>
                            <Chip
                              label={mold.status}
                              size="small"
                              sx={{ mt: 1, borderRadius: 1 }}
                            />
                          </Box>
                          <Box textAlign={{ xs: 'left', sm: 'right' }}>
                            <Typography variant="caption" color="text.secondary">
                              수명률
                            </Typography>
                            <Typography
                              variant="h5"
                              fontWeight={600}
                              color={lifePercent > 80 ? 'error.main' : 'success.main'}
                              sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
                            >
                              {lifePercent}%
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
        </>
      )}
    </Box>
  );
};
